import React, { useContext, useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import SearchIcon from "@material-ui/icons/Search";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MaskUtils from "../../utils/classes/MaskUtils";
import PixMessagesModal from "../../components/PixMessagesModal";
import SocketPixMessages from "../../context/Socket/Listeners/SocketPixMessages";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_PIX_MESSAGES") {
    const pixMessages = action.payload;
    const newPixMessages = [];

    pixMessages.forEach(pixMessage => {
      const pixMessageIndex = state.findIndex(pm => pm.id === pixMessage.id);
      if (pixMessageIndex !== -1) state[pixMessageIndex] = pixMessage;
      else newPixMessages.push(pixMessage);
    });

    return [...state, ...newPixMessages];
  }

  if (action.type === "UPDATE_PIX_MESSAGES") {
    const pixMessage = action.payload;
    const pixMessageIndex = state.findIndex(pm => pm.id === pixMessage.id);

    if (pixMessageIndex !== -1) {
      state[pixMessageIndex] = pixMessage;
      return [...state];
    }
    else {
      return [pixMessage, ...state];
    }
  }

  if (action.type === "DELETE_PIX_MESSAGE") {
    const pixMessageId = action.payload;
    const pixMessageIndex = state.findIndex(pm => pm.id === pixMessageId);
    if (pixMessageIndex !== -1) state.splice(pixMessageIndex, 1);
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  interactiveTableCell: { cursor: "pointer", },

  icon: { color: theme.palette.text.primary, },

  floatingButton: {
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  actionButton: {
    "&:hover": { color: theme.palette.primary.main },
  },
}));

const PixMessages = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [pixMessages, dispatch] = useReducer(reducer, []);

  const [selectedPixMessage, setSelectedPixMessage] = useState(null);
  const [pixMessageModalOpen, setPixMessageModalOpen] = useState(false);
  const [deletingPixMessage, setDeletingPixMessage] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (user.profile === "user" || !user.configEnabled) {
      DefaultFunctionsUtils.redirectToTicketsPage(history);
      setShouldSyncRouteOnMenu(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchPixMessages = async () => {
        try {
          const { data } = await api.get("/pixMessages", { params: { searchParam, pageNumber } });

          dispatch({ type: "LOAD_PIX_MESSAGES", payload: data.pixMessages });
          setHasMore(data.hasMore);
          setLoading(false);
        }
        catch (exception) {
          console.log("Pix Messages Use Effect 1 Exception:", exception);
          toastError(exception);
        }
      };

      fetchPixMessages();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleSearchParam = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenPixMessageModal = () => {
    setSelectedPixMessage(null);
    setPixMessageModalOpen(true);
  };

  const handleEditPixMessageModal = (pixMessage) => {
    setSelectedPixMessage(pixMessage);
    setPixMessageModalOpen(true);
  };

  const handleClosePixMessageModal = () => {
    setPixMessageModalOpen(false);

    setTimeout(() => {
      setSelectedPixMessage(null);
    }, 500);
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber(previousPageNumber => previousPageNumber + 1);
  };

  const handleOpenConfirmModal = (pixMessage) => {
    setConfirmModalOpen(true);
    setDeletingPixMessage(pixMessage);
  };

  const handleDeletePixMessage = async (pixMessageId) => {
    try {
      await api.delete(`/pixMessages/${pixMessageId}`);
    }
    catch (exception) {
      console.log("Handle Delete Pix Message Exception:", exception);
      toastError(exception);
    }
    finally {
      setDeletingPixMessage(null);
      setSearchParam("");
      setPageNumber(1);
    }
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <MainContainer>
      {/* 
        ************
        ** Socket **
        ************
      */}
      <SocketPixMessages dispatch={dispatch} />



      {/* 
        ************
        ** Modals **
        ************
      */}
      <ConfirmationModal
        title={i18n.t("pixMessages.confirmationModal.deleteTitle")}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={async () => await handleDeletePixMessage(deletingPixMessage.id)}
      />

      <PixMessagesModal
        open={pixMessageModalOpen}
        onClose={handleClosePixMessageModal}
        pixMessageId={selectedPixMessage && selectedPixMessage.id}
      />



      {/* 
        ************
        ** Header **
        ************
      */}
      <MainHeader>
        <Title>{i18n.t("pixMessages.title")}</Title>

        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("pixMessages.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearchParam}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon style={{ color: "gray" }} /></InputAdornment>) }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenPixMessageModal}
            className={classes.floatingButton}
          >
            {i18n.t("pixMessages.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>



      {/* 
        ***********
        ** Paper **
        ***********
      */}
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("pixMessages.table.key")}</TableCell>
              <TableCell align="center">{i18n.t("pixMessages.table.type")}</TableCell>
              <TableCell align="center">{i18n.t("pixMessages.table.name")}</TableCell>
              <TableCell align="center">{i18n.t("pixMessages.table.actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pixMessages.map(pixMessage => (
              <TableRow key={pixMessage.id}>
                <TableCell align="center">{MaskUtils.maskText(pixMessage.key, pixMessage.type)}</TableCell>
                <TableCell align="center">{i18n.t(`pixMessagesModal.form.types.${pixMessage.type}`)}</TableCell>
                <TableCell align="center">{pixMessage.name}</TableCell>
                <TableCell align="center">
                  <IconButton
                    className={classes.icon}
                    size="small"
                    onClick={() => handleEditPixMessageModal(pixMessage)}
                  >
                    <EditIcon className={classes.actionButton} />
                  </IconButton>

                  <IconButton
                    className={classes.icon}
                    size="small"
                    onClick={() => handleOpenConfirmModal(pixMessage)}
                  >
                    <DeleteOutlineIcon className={classes.actionButton} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {loading && <TableRowSkeleton columns={4} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default PixMessages;
